import React, { useState, useEffect } from 'react';
import { Plus, Building, Edit, Trash2, Eye, Users } from 'lucide-react';
import { Card, Button, Input, Select, Table, Modal, Badge, ConfirmDialog } from '../components/ui';
import { departamentosService } from '../services/api';
import { formatMoney } from '../utils/formatters';
import { TIPOS_DEPARTAMENTO, ESTADOS_DEPARTAMENTO } from '../utils/constants';
import toast from 'react-hot-toast';

const DepartamentosPage = () => {
  const [departamentos, setDepartamentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({
    numero: '',
    piso: '',
    propietario: '',
    email: '',
    telefono: '',
    cuota: 350,
    tipo: 'DEPARTAMENTO',
    estado: 'ACTIVO'
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await departamentosService.getAll();
      setDepartamentos(response.data.data || []);
    } catch (error) {
      toast.error('Error al cargar departamentos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selected) {
        await departamentosService.update(selected.id, form);
        toast.success('Departamento actualizado');
      } else {
        await departamentosService.create(form);
        toast.success('Departamento creado');
      }
      setModalOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error al guardar');
    }
  };

  const handleEdit = (dpto) => {
    setSelected(dpto);
    setForm({
      numero: dpto.numero,
      piso: dpto.piso,
      propietario: dpto.propietario,
      email: dpto.email || '',
      telefono: dpto.telefono || '',
      cuota: dpto.cuota,
      tipo: dpto.tipo,
      estado: dpto.estado
    });
    setModalOpen(true);
  };

  const handleView = async (dpto) => {
    try {
      const response = await departamentosService.getById(dpto.id);
      setSelected(response.data.data);
      setDetailOpen(true);
    } catch (error) {
      toast.error('Error al cargar detalles');
    }
  };

  const handleDelete = async () => {
    try {
      await departamentosService.delete(selected.id);
      toast.success('Departamento eliminado');
      setConfirmOpen(false);
      loadData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const resetForm = () => {
    setSelected(null);
    setForm({ numero: '', piso: '', propietario: '', email: '', telefono: '', cuota: 350, tipo: 'DEPARTAMENTO', estado: 'ACTIVO' });
  };

  const statusVariant = { ACTIVO: 'success', MOROSO: 'danger', INACTIVO: 'default' };

  const columns = [
    { header: 'Nº', render: (row) => <span className="font-bold text-emerald-400">{row.numero}</span> },
    { header: 'Piso', accessor: 'piso' },
    { header: 'Propietario', accessor: 'propietario' },
    { header: 'Cuota', render: (row) => formatMoney(row.cuota) },
    { header: 'Estado', render: (row) => <Badge variant={statusVariant[row.estado]}>{row.estado}</Badge> },
    { header: 'Acciones', render: (row) => (
      <div className="flex gap-2">
        <button onClick={() => handleView(row)} className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-blue-400"><Eye size={16} /></button>
        <button onClick={() => handleEdit(row)} className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-white"><Edit size={16} /></button>
        <button onClick={() => { setSelected(row); setConfirmOpen(true); }} className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-rose-400"><Trash2 size={16} /></button>
      </div>
    )}
  ];

  const stats = {
    total: departamentos.length,
    activos: departamentos.filter(d => d.estado === 'ACTIVO').length,
    morosos: departamentos.filter(d => d.estado === 'MOROSO').length
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Departamentos</h1>
          <p className="text-slate-400">Gestión de unidades del edificio</p>
        </div>
        <Button icon={Plus} onClick={() => { resetForm(); setModalOpen(true); }}>Nuevo Departamento</Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-500/20"><Building className="text-blue-400" size={20} /></div>
            <div>
              <p className="text-sm text-slate-400">Total</p>
              <p className="text-xl font-bold text-white">{stats.total}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-emerald-500/20"><Users className="text-emerald-400" size={20} /></div>
            <div>
              <p className="text-sm text-slate-400">Al día</p>
              <p className="text-xl font-bold text-white">{stats.activos}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-rose-500/20"><Building className="text-rose-400" size={20} /></div>
            <div>
              <p className="text-sm text-slate-400">Morosos</p>
              <p className="text-xl font-bold text-white">{stats.morosos}</p>
            </div>
          </div>
        </Card>
      </div>

      <Card className="overflow-hidden">
        <Table columns={columns} data={departamentos} loading={loading} />
      </Card>

      {/* Modal Form */}
      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={selected ? 'Editar Departamento' : 'Nuevo Departamento'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input label="Número" value={form.numero} onChange={(e) => setForm({ ...form, numero: e.target.value })} required />
            <Input label="Piso" type="number" value={form.piso} onChange={(e) => setForm({ ...form, piso: e.target.value })} required />
            <Input label="Propietario" value={form.propietario} onChange={(e) => setForm({ ...form, propietario: e.target.value })} required className="md:col-span-2" />
            <Input label="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <Input label="Teléfono" value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} />
            <Input label="Cuota (S/)" type="number" value={form.cuota} onChange={(e) => setForm({ ...form, cuota: e.target.value })} required />
            <Select label="Tipo" value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })} options={TIPOS_DEPARTAMENTO} />
            <Select label="Estado" value={form.estado} onChange={(e) => setForm({ ...form, estado: e.target.value })} options={ESTADOS_DEPARTAMENTO} />
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button type="submit">{selected ? 'Actualizar' : 'Guardar'}</Button>
          </div>
        </form>
      </Modal>

      {/* Modal Detail */}
      <Modal isOpen={detailOpen} onClose={() => setDetailOpen(false)} title={`Departamento ${selected?.numero}`} size="lg">
        {selected && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div><p className="text-slate-400 text-sm">Propietario</p><p className="text-white font-medium">{selected.propietario}</p></div>
              <div><p className="text-slate-400 text-sm">Estado</p><Badge variant={statusVariant[selected.estado]}>{selected.estado}</Badge></div>
              <div><p className="text-slate-400 text-sm">Email</p><p className="text-white">{selected.email || '-'}</p></div>
              <div><p className="text-slate-400 text-sm">Teléfono</p><p className="text-white">{selected.telefono || '-'}</p></div>
              <div><p className="text-slate-400 text-sm">Cuota</p><p className="text-white font-medium">{formatMoney(selected.cuota)}</p></div>
              <div><p className="text-slate-400 text-sm">Tipo</p><p className="text-white">{selected.tipo}</p></div>
            </div>
            {selected.ingresos && selected.ingresos.length > 0 && (
              <div>
                <h4 className="text-white font-semibold mb-3">Últimos Pagos</h4>
                <div className="space-y-2">
                  {selected.ingresos.slice(0, 5).map((ing, i) => (
                    <div key={i} className="flex justify-between p-3 rounded-lg bg-slate-700/30">
                      <span className="text-slate-300">{ing.concepto}</span>
                      <span className="text-emerald-400 font-medium">{formatMoney(ing.monto)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      <ConfirmDialog isOpen={confirmOpen} onClose={() => setConfirmOpen(false)} onConfirm={handleDelete} title="Eliminar Departamento" />
    </div>
  );
};

export default DepartamentosPage;
